import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useState, useRef, useEffect } from 'react';
import { Button } from './Button';

export const Header = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isCoursesDropdownOpen, setIsCoursesDropdownOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const coursesDropdownRef = useRef<HTMLDivElement>(null);

  // Scroll detection for header shadow
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
  };

  const toggleCoursesDropdown = () => {
    setIsCoursesDropdownOpen(!isCoursesDropdownOpen);
  };

  const closeCoursesDropdown = () => {
    setIsCoursesDropdownOpen(false);
  };

  // Click outside handler
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (coursesDropdownRef.current && !coursesDropdownRef.current.contains(event.target as Node)) {
        closeCoursesDropdown();
      }
    };

    if (isCoursesDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isCoursesDropdownOpen]);

  const isActive = (path: string) => {
    return location.pathname === path;
  };

  const courseLinks = [
    { title: 'CPR & AED Certification', path: '/courses/cpr-aed' },
    { title: 'CPR Renewal', path: '/courses/cpr-renewal' },
    { title: 'BLS for Healthcare', path: '/courses/bls' },
    { title: 'Group Training', path: '/courses/group-training' },
    { title: 'View All Courses', path: '/courses' },
  ];

  return (
    <header className={`bg-white sticky top-0 z-50 transition-all duration-200 ${isScrolled ? 'shadow-[0_4px_0_0_rgba(0,60,102,0.2)] border-b-4 border-indigo_dye-500' : 'border-b-4 border-transparent'}`}>
      <nav className="container mx-auto px-4 py-4">
        <div className="flex justify-between items-center">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3 hover:opacity-90 transition-opacity">
            <img
              src="/CRPTrainingDirectLogo.png"
              alt="CPR Training Direct Logo"
              className="h-12 w-auto"
            />
            <span className="text-2xl font-bold text-indigo_dye-500 hidden sm:block">
              CPR Training Direct
            </span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center space-x-8">
            {/* Home */}
            <Link
              to="/"
              className={`text-gray-700 hover:text-indigo_dye-600 transition-colors font-medium ${
                isActive('/') ? 'text-indigo_dye-600 border-b-2 border-indigo_dye-600' : ''
              }`}
            >
              Home
            </Link>

            {/* Courses Dropdown */}
            <div
              ref={coursesDropdownRef}
              className="relative"
            >
              <button
                onClick={toggleCoursesDropdown}
                className={`text-gray-700 hover:text-indigo_dye-600 transition-colors font-medium flex items-center ${
                  location.pathname.startsWith('/courses') ? 'text-indigo_dye-600' : ''
                }`}
                aria-expanded={isCoursesDropdownOpen}
                aria-haspopup="true"
              >
                Courses
                <svg
                  className={`ml-1 h-4 w-4 transition-transform ${
                    isCoursesDropdownOpen ? 'rotate-180' : ''
                  }`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>

              {/* Dropdown Menu - Brutalist */}
              {isCoursesDropdownOpen && (
                <div className="absolute left-0 mt-2 w-64 bg-white border-4 border-rich_black-500 shadow-[4px_4px_0_0_rgba(2,5,18,1)] py-2 z-50">
                  {courseLinks.map((course) => (
                    <Link
                      key={course.path}
                      to={course.path}
                      onClick={closeCoursesDropdown}
                      className={`block px-4 py-3 font-body text-gray-700 hover:bg-indigo_dye-50 hover:text-indigo_dye-600 transition-colors border-b border-gray-100 last:border-b-0 ${
                        isActive(course.path) ? 'bg-indigo_dye-50 text-indigo_dye-600 border-l-4 border-l-indigo_dye-600' : ''
                      }`}
                    >
                      {course.title}
                    </Link>
                  ))}
                </div>
              )}
            </div>

            {/* FAQ */}
            <Link
              to="/resources"
              className={`text-gray-700 hover:text-indigo_dye-600 transition-colors font-medium ${
                isActive('/resources') ? 'text-indigo_dye-600 border-b-2 border-indigo_dye-600' : ''
              }`}
            >
              FAQ
            </Link>

            {/* Contact */}
            <Link
              to="/contact"
              className={`text-gray-700 hover:text-indigo_dye-600 transition-colors font-medium ${
                isActive('/contact') ? 'text-indigo_dye-600 border-b-2 border-indigo_dye-600' : ''
              }`}
            >
              Contact
            </Link>

            {/* Book Now CTA - Brutalist */}
            <Button
              variant="primary"
              size="md"
              onClick={() => navigate('/book')}
            >
              Book Now
            </Button>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="lg:hidden text-gray-700 hover:text-indigo_dye-600 transition-colors"
            onClick={toggleMobileMenu}
            aria-label="Toggle mobile menu"
          >
            <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>
        </div>
      </nav>

      {/* Mobile Menu Overlay */}
      {isMobileMenuOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={closeMobileMenu}
        ></div>
      )}

      {/* Mobile Menu Drawer - Brutalist */}
      <div
        className={`fixed top-0 right-0 h-full w-80 bg-white border-l-4 border-rich_black-500 shadow-[-8px_0_0_0_rgba(2,5,18,0.2)] z-50 transform transition-transform duration-300 ease-in-out lg:hidden ${
          isMobileMenuOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        {/* Mobile Menu Header */}
        <div className="flex justify-between items-center p-4 border-b-4 border-gray-200">
          <span className="font-display text-xl font-bold uppercase tracking-wide text-indigo_dye-500">Menu</span>
          <button
            onClick={closeMobileMenu}
            className="text-gray-700 hover:text-indigo_dye-600 transition-colors"
            aria-label="Close menu"
          >
            <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Mobile Menu Items */}
        <div className="overflow-y-auto h-full pb-20">
          <div className="py-4">
            {/* Home */}
            <Link
              to="/"
              onClick={closeMobileMenu}
              className={`block px-6 py-3 text-gray-700 hover:bg-indigo_dye-50 hover:text-indigo_dye-600 transition-colors font-medium ${
                isActive('/') ? 'bg-indigo_dye-50 text-indigo_dye-600 border-l-4 border-indigo_dye-600' : ''
              }`}
            >
              Home
            </Link>

            {/* Courses Section */}
            <div className="border-t border-gray-100 mt-2 pt-2">
              <div className="px-6 py-2 text-sm font-semibold text-gray-500 uppercase tracking-wider">
                Courses
              </div>
              {courseLinks.map((course) => (
                <Link
                  key={course.path}
                  to={course.path}
                  onClick={closeMobileMenu}
                  className={`block px-6 py-3 text-gray-700 hover:bg-indigo_dye-50 hover:text-indigo_dye-600 transition-colors ${
                    isActive(course.path) ? 'bg-indigo_dye-50 text-indigo_dye-600 border-l-4 border-indigo_dye-600' : ''
                  }`}
                >
                  {course.title}
                </Link>
              ))}
            </div>

            {/* Other Links */}
            <Link
              to="/resources"
              onClick={closeMobileMenu}
              className={`block px-6 py-3 text-gray-700 hover:bg-indigo_dye-50 hover:text-indigo_dye-600 transition-colors font-medium ${
                isActive('/resources') ? 'bg-indigo_dye-50 text-indigo_dye-600 border-l-4 border-indigo_dye-600' : ''
              }`}
            >
              FAQ
            </Link>

            <Link
              to="/contact"
              onClick={closeMobileMenu}
              className={`block px-6 py-3 text-gray-700 hover:bg-indigo_dye-50 hover:text-indigo_dye-600 transition-colors font-medium ${
                isActive('/contact') ? 'bg-indigo_dye-50 text-indigo_dye-600 border-l-4 border-indigo_dye-600' : ''
              }`}
            >
              Contact
            </Link>

            {/* Book Now CTA - Brutalist */}
            <div className="px-6 py-4">
              <Button
                variant="primary"
                size="lg"
                className="w-full"
                onClick={() => {
                  closeMobileMenu();
                  navigate('/book');
                }}
              >
                Book Now
              </Button>
            </div>
          </div>
        </div>
      </div>
    </header>
  );
};
